import { useSwitchChain } from '@wagmi/vue';
import { type Address } from 'viem';
import { storeToRefs } from 'pinia';
import { useProviderConfig, useState } from '#imports';
import { useAlertsStore } from '~/stores/alerts/useAlertsStore';
import { useCartStore } from '~/stores/cart/useCartStore';
import { useTransactionStore } from '~/stores/transaction/useTransactionStore';
import { useUserStore } from '~/stores/user/useUserStore';
import { AsyncState } from '~/types/enums';
import type { EvmFullFillOrderType } from '~/service/EVM/types';
import useEVMApi from '~/composables/useApi/useEVMApi';
import type { EvmFullFillFetchedOrder } from '~/service/api/types';

type ABIInput = {
    internalType: string;
    name: string;
    type: string;
};

type ABIEntry = {
    inputs: ABIInput[];
    stateMutability: 'pure' | 'view' | 'nonpayable' | 'payable';
    type: 'function' | 'constructor' | 'event' | 'fallback';
};

type Args = (string | number | boolean | bigint)[];

type FormattedArgs = Record<string, string | number | boolean | bigint>;

type ChainError = {
    message: string;
    cause: string;
    details?: string;
    docsPath: string;
    metaMessages: string[];
    shortMessage: string;
    version: number;
    name: string;
    abi: ABIEntry[];
    args: Args[];
    contractAddress: string;
    formattedArgs: FormattedArgs;
    functionName: string;
    sender: string;
};

const handleResponseOrderErrors = (data: EvmFullFillFetchedOrder[], paymentValues: AppCartItemType[]) => {
    return data.reduce(
        (acc, item, index) => {
            if (item.error) {
                acc.withError.push(item);
            }
            else {
                const { to } = item.order;
                if (!acc.withoutError[to]) {
                    acc.withoutError[to] = [];
                }
                acc.withoutError[to].push({
                    ...item,
                    amount1155: paymentValues[index]?.amount1155
                });
            }
            return acc;
        },
        {
            withError: [] as EvmFullFillFetchedOrder[],
            withoutError: {} as Record<EvmFullFillFetchedOrder['order']['to'], EvmFullFillOrderType[]>
        }
    );
};

export default function useBuyNft() {
    const userStore = useUserStore();
    const cartStore = useCartStore();
    const alertStore = useAlertsStore();
    const evmCore = useEVMCore();

    const loadingState = useState<Record<AppWTFNft['uuid'], AsyncState>>(() => ({}));

    const { map: { chainMap } } = useProviderConfig()(['map', 'contract']);

    const { cart } = storeToRefs(cartStore);

    const { chainId, user, network } = storeToRefs(userStore);

    const { addTransaction } = useTransactionStore();

    const { order } = useEVMApi();

    const { switchChainAsync } = useSwitchChain();

    const { $provider } = useNuxtApp();

    const buyNFT = async (items?: AppCartItemType[], transactionType?: AppTransaction['type']) => {
        if (!user.value) return userStore.showUserIsDisconnect();

        const paymentValues = items?.length
            ? items
            : [...cart.value.values()];

        const paymentNetwork = paymentValues[0]?.network;

        const paymentUuids = paymentValues.map(item => item.uuid);

        paymentUuids.forEach((uuid) => {
            loadingState.value[uuid] = AsyncState.IS_LOADING;
        });

        const response = await order.bestOrders(
            paymentUuids
        ).then(data => data)
            .catch(() => {
                loadingState.value = {};
                return alertStore.add({
                    title: 'Oops, something went wrong',
                    description: 'Error when creating an order',
                    critical: true,
                    status: 'type.error'
                });
            });

        if (response) {
            const {
                withError,
                withoutError
            } = handleResponseOrderErrors(response.data, paymentValues);

            if (withError.length) {
                withError.forEach((item) => {
                    cartStore.changeCartItem(
                        item.uuid,
                        { isError: true }
                    );
                    loadingState.value[item.uuid] = AsyncState.IS_ERROR;
                    alertStore.add({
                        title: 'Oops, something went wrong',
                        description: `${item.error}`,
                        critical: false,
                        status: 'type.error'
                    });
                });
            }

            const protocolOrders = Object.entries(withoutError);

            if (protocolOrders.length && paymentNetwork) {
                return await sendBuyTransactions({ protocolOrders, paymentNetwork, paymentValues, transactionType });
            }
        }
    };

    const sendBuyTransactions = async ({
        protocolOrders,
        paymentNetwork,
        paymentValues,
        transactionType
    }: {
        protocolOrders: [string, EvmFullFillOrderType[]][];
        paymentNetwork: User['network'];
        paymentValues: AppCartItemType[];
        transactionType?: AppTransaction['type'];
    }) => {
        try {
            return await evmCore.eventEmitter.emit('purchase', async () => {
                const nftBuyer = evmCore.createEvmNFTBuyer({
                    switchNetworkFn: switchChainAsync,
                    user: {
                        address: user.value,
                        chainId: chainId.value
                    },
                    ...$provider
                });

                const transactionProtocolPromises = protocolOrders.map(async ([to, data], index) => {
                    data.forEach((item) => {
                        if (loadingState.value[item.uuid] !== AsyncState.IS_LOADING) {
                            loadingState.value[item.uuid] = AsyncState.IS_LOADING;
                        }
                    });

                    if (user.value && chainId.value && network.value && paymentNetwork) {
                        const transaction = await nftBuyer.buyNft({
                            marketplaceContractAddress: to as Address,
                            fullFillOrders: data,
                            paramChainId: chainMap[paymentNetwork].id
                        }).then((response) => {
                            data.forEach((item) => {
                                loadingState.value[item.uuid] = AsyncState.IS_SUCCESS;
                            });
                            return response;
                        }).catch((err) => {
                            const error = err as Error;
                            data.forEach((item) => {
                                loadingState.value[item.uuid] = AsyncState.IS_ERROR;
                            });
                            throw new Error(error.message);
                        });

                        if (transaction) {
                            addTransaction({
                                hash: transaction.value,
                                chainId: chainId.value,
                                tokenList: paymentValues,
                                value: transaction.sum,
                                currency: network.value,
                                status: 'pending',
                                type: transactionType ?? 'cart',
                                typeTransaction: 'purchase'
                            });
                        }

                        return transaction;
                    }
                });

                return await Promise.all(transactionProtocolPromises).then(values => values.filter(transaction => !!transaction));
            });
        }
        catch (err) {
            const error = err as ChainError;
            if (error.shortMessage?.includes('transfer amount exceeds allowance')) {
                alertStore.add({
                    title: 'Execution reverted',
                    description: 'Transfer amount exceeds allowance',
                    critical: false,
                    status: 'type.error'
                });
            }
            else {
                alertStore.add({
                    title: 'Oops, something went wrong',
                    description: error.message,
                    critical: false,
                    status: 'type.error'
                });
            }
            protocolOrders.forEach(([_, data]) => data.forEach((item) => {
                loadingState.value[item.uuid] = AsyncState.IS_ERROR;
            }));
            throw err;
        }
        finally {
            const resetLoadingState = setTimeout(
                () => {
                    loadingState.value = {};
                },
                1000
            );
            clearTimeout(resetLoadingState);
        }
    };

    return {
        loadingState,
        buyNFT,
        sendBuyTransactions,
        AsyncState
    };
}
